import { useUpdateTasks } from 'features/tasks';
import React, { useMemo, useRef } from 'react';
import { NewTaskRow } from 'shared/components/connected/new-task-row';
import {
  RenderItemProps,
  rootId,
  TreeItem,
  TreeItemTypes,
  useTree,
  VirtualizedTreeList,
} from 'shared/components/ui/virtualized-tree-list';
import { useTaskDateShortcuts } from 'shared/hooks/use-task-date-shortcuts';
import { useTaskPriorityShortcuts } from 'shared/hooks/use-task-priority-shortcuts';
import { FilterDocument, FilterOptionKeys } from 'shared/types/filter-document';
import { ID } from 'shared/types/id';
import { RelationshipUpdate } from 'shared/types/relationship';
import { Task } from 'shared/types/task';
import { DueDisplayOptions } from 'shared/types/task-base';
import { Timestamp } from 'shared/types/timestamp';

import { noGroupingId } from '../../types';
import { TreeTaskCard } from '../tree-task-card';
import * as Styled from './no-grouping-tasks-list.style';

export type NoGroupingTaskListRendererProps = {
  treeData: Record<ID, TreeItem>;
  tasks: Task[];
  onReorder?: (
    updatedSections: { id: ID; taskSorting: ID[] }[],
    updatedTask: Task[],
    relationshipUpdates?: RelationshipUpdate[],
  ) => void;
  collapsedSections?: ID[];
  onSectionCollapse?: (sectionIds: ID[]) => void;
  scrollElement?: React.RefObject<HTMLDivElement>;
  denseFormat?: boolean;
  filterDocument?: FilterDocument;
  isContextMenuOpen?: boolean;
  defaultNewTaskDate?: Timestamp;
  goalId?: ID;
  lifeAreaId?: ID;
  tagId?: ID;
  displayDue?: DueDisplayOptions[];
};

const taskTreeItemTypes = [TreeItemTypes.Task];

const dropTypes = {
  [TreeItemTypes.Root]: [TreeItemTypes.Task],
  [TreeItemTypes.Goal]: [],
  [TreeItemTypes.Task]: [],
  [TreeItemTypes.LifeArea]: [],
  [TreeItemTypes.Priority]: [],
  [TreeItemTypes.Date]: [],
  [TreeItemTypes.DayPart]: [],
  [TreeItemTypes.Tag]: [],
  [TreeItemTypes.Type]: [],
  [TreeItemTypes.NewTask]: [],
  [TreeItemTypes.Section]: [],
};

export const NoGroupingTaskListRenderer: React.FC<
  NoGroupingTaskListRendererProps
> = ({
  treeData,
  tasks,
  onReorder,
  collapsedSections,
  onSectionCollapse,
  scrollElement,
  denseFormat,
  filterDocument,
  isContextMenuOpen,
  defaultNewTaskDate,
  goalId,
  lifeAreaId,
  tagId,
  displayDue,
}) => {
  const tasksMap = useMemo(() => {
    return tasks.reduce<Record<ID, Task>>((acc, task) => {
      acc[task.id] = task;
      return acc;
    }, {});
  }, [tasks]);

  const _onReorder = (updatedItems: TreeItem[]) => {
    if (!onReorder) {
      return;
    }
    const updatedRoot = updatedItems.find(({ id }) => id === rootId);
    if (!updatedRoot) {
      return;
    }

    onReorder(
      [
        {
          id: noGroupingId,
          taskSorting: updatedRoot.children
            ? updatedRoot.children.filter((id) => !id.endsWith('-new-task'))
            : [],
        },
      ],
      [],
    );
  };

  // using refs to prevent the renderItem from rerendering all items
  const tasksMapRef = useRef(tasksMap);
  tasksMapRef.current = tasksMap;

  const tasksRef = useRef(tasks);
  tasksRef.current = tasks;

  const renderItem: React.FC<RenderItemProps> = useMemo(
    () =>
      ({ item, tree, itemInstance, isMultiSelectModifierPressed }) => {
        if (item.type === TreeItemTypes.NewTask) {
          // extract the goal, lifeArea and tag defaults from the filterDocument
          // checks if there is valid array of length 1 for the includelist
          const goalFilterId =
            filterDocument?.filters?.[FilterOptionKeys.Goal]?.include
              ?.length === 1
              ? filterDocument?.filters?.[FilterOptionKeys.Goal]?.include?.[0]
              : undefined;

          const lifeAreaFilterId =
            filterDocument?.filters?.[FilterOptionKeys.LifeArea]?.include
              ?.length === 1
              ? filterDocument?.filters?.[FilterOptionKeys.LifeArea]
                  ?.include?.[0]
              : undefined;

          const tagIds =
            filterDocument?.filters?.[FilterOptionKeys.Tag]?.include?.length ===
            1
              ? filterDocument?.filters?.[FilterOptionKeys.Tag]?.include
              : undefined;

          return (
            <NewTaskRow
              goal={goalId ?? goalFilterId}
              lifeArea={lifeAreaId ?? lifeAreaFilterId}
              tags={tagId ? [tagId] : tagIds}
              startTime={null}
              duration={null}
              date={defaultNewTaskDate}
            />
          );
        }

        const currentTasksMap = tasksMapRef.current;
        const currentTasks = tasksRef.current;

        return currentTasksMap[item.sourceId] ? (
          <TreeTaskCard
            task={currentTasksMap[item.sourceId]}
            tree={tree}
            item={itemInstance}
            tasks={currentTasks}
            isMultiSelectModifierPressed={isMultiSelectModifierPressed}
            displayDue={displayDue}
          />
        ) : null;
      },
    [
      defaultNewTaskDate,
      filterDocument?.filters,
      goalId,
      lifeAreaId,
      tagId,
      displayDue,
    ],
  );

  const [tree] = useTree();
  const { submit: updateTasks } = useUpdateTasks();

  useTaskPriorityShortcuts(tree, tasks, updateTasks);
  useTaskDateShortcuts(tree, tasks, updateTasks);

  return (
    <Styled.Container>
      <VirtualizedTreeList
        rootItemId={rootId}
        items={treeData}
        renderItem={renderItem}
        scrollElement={scrollElement}
        multiselectTypes={taskTreeItemTypes}
        dragTypes={taskTreeItemTypes}
        onReorder={_onReorder}
        dropTypes={dropTypes}
        collapsedItems={collapsedSections}
        onCollapse={onSectionCollapse}
        denseFormat={denseFormat}
        isContextMenuOpen={isContextMenuOpen}
      />
    </Styled.Container>
  );
};
